"use client";

import { AxiosError } from "axios";
import api from "@/app/axios/axiosInstance";

export interface UserProfile {
  email: string;
  id: string;
  name: string;
  role: string;
  jabatan?: string;
}

export interface Role {
  id: string;
  name: string;
}

export interface Pengguna {
  id?: string;
  name?: string;
  nama?: string;
  email?: string;
  role_name?: string;
  is_active?: boolean;
  created_at?: string;
}

export interface Personel {
  id: string;
  pangkat?: string;
  nrp_nip?: string;
  jabatan_id?: string;
  jabatan?: string;
  satuan_id: string;
  satuan_detail: string;
  satuan_kode:string;
  full_name?: string; // ← tambah
  user_id?: string;
  user_name?: string;
  photo_url?: string;
  created_at?: string; // ← tambah
}

export interface Profile {
  email: string;
  id: string;
  name: string;
  role: string;
}

export interface UpdatePersonelRequest {
  jabatan?: string;
  full_name?: string;
}

export interface ChangePasswordRequest {
  current_password: string;
  new_password: string;
}

export interface CreatePersonelRequest {
  pangkat: string;
  nrp_nip: string;
  full_name: string;
  jabatan: string;
  satuan_id: string;
  email: string;
  role_id: string;
}

export interface CreatePersonelResponse {
  success: boolean;
  status: number;
  message?: string;
  data?: unknown;
}

export interface ActionResponse {
  success: boolean;
  status: number;
  message?: string;
  data?: unknown;
}

interface ErrorResponse {
  message?: string;
  error?: string; // ← backend Go mengirim key ini untuk error 500 (mis. duplicate key constraint)
  errors?: Record<string, string[] | string>;
}

export interface ImageUploadResponse {
  content_type: string;
  photo_url: string;
  size: number;
}

// Tambahkan interface ini
export interface PersonelPhotoResponse {
  photo_url: string;
  content_type: string;
  size: number;
}

const formatError = (error: AxiosError<ErrorResponse>): string => {
  const data = error.response?.data;

  if (data?.errors) {
    const messages = Object.values(data.errors).flat().filter(Boolean);
    if (messages.length) return messages.join(", ");
  }

  const rawMessage = data?.message || data?.error;

  // Backend sering mengembalikan status 500 untuk pelanggaran unique
  // constraint (harusnya 409 Conflict), jadi kita deteksi dari isi pesan
  // mentahnya juga, bukan cuma dari status code.
  const isConflict =
    error.response?.status === 409 ||
    (rawMessage && /duplicate key|unique constraint/i.test(rawMessage));

  if (isConflict) {
    if (rawMessage?.includes("users_email_key")) {
      return "Email sudah terdaftar. Gunakan email lain.";
    }
    if (rawMessage?.includes("nrp_nip")) {
      return "NRP/NIP sudah terdaftar.";
    }
    return "Data ini sudah terdaftar sebelumnya.";
  }

  return rawMessage || "Terjadi kesalahan";
};

export const userServices = {
  getCurrentPersonel: async (): Promise<Personel> => {
    try {
      const response = await api.get<Personel>("/personel/me");
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response?.status === 401) {
        throw new Error("Sesi telah berakhir. Silakan login kembali.");
      }
      throw new Error(formatError(axiosError));
    }
  },
  getProfile: async (): Promise<Profile> => {
    try {
      const response = await api.get("/profile");
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<ErrorResponse>;
      throw new Error(formatError(axiosError));
    }
  },
  getPersonelById: async (id: string): Promise<Personel> => {
    try {
      const response = await api.get(`/personel/${id}`);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<ErrorResponse>;
      throw new Error(formatError(axiosError));
    }
  },
  getRoles: async (): Promise<Role[]> => {
    try {
      const response = await api.get("/admin/roles");
      const data = response.data?.data ?? response.data;
      return Array.isArray(data) ? data : [];
    } catch (error) {
      const axiosError = error as AxiosError<ErrorResponse>;
      throw new Error(formatError(axiosError));
    }
  },

  getAll: async (): Promise<Pengguna[]> => {
    try {
      const response = await api.get("/admin/users");
      const data = response.data?.data ?? response.data;
      return Array.isArray(data) ? data : [];
    } catch (error) {
      const axiosError = error as AxiosError<ErrorResponse>;
      throw new Error(formatError(axiosError));
    }
  },

  getPersonel: async (): Promise<Personel[]> => {
    try {
      const response = await api.get("/personel");
      const data = response.data?.data ?? response.data;
      return Array.isArray(data) ? data : [];
    } catch (error) {
      const axiosError = error as AxiosError<ErrorResponse>;
      throw new Error(formatError(axiosError));
    }
  },

  // Tambah pengguna: buat akun -> buat personel
  createPersonel: async (
    data: CreatePersonelRequest,
  ): Promise<CreatePersonelResponse> => {
    try {
      // 1) Buat akun user -> /admin/users
      const response = await api.post("/personel", {
        pangkat: data.pangkat,
        nrp_nip: data.nrp_nip,
        full_name: data.full_name,
        jabatan: data.jabatan,
        satuan_id: data.satuan_id,
        email: data.email,
        role_id: data.role_id,
      });
      return { ...response.data, success: true, status: response.status };
    } catch (error) {
      const axiosError = error as AxiosError<ErrorResponse>;
      return {
        success: false,
        status: axiosError.response?.status || 500,
        message: formatError(axiosError),
      };
    }
  },

  // Hapus pengguna: hapus personel (jika ada) lalu akun user
  nonActivePersonel: async (
    userId: string
  ): Promise<ActionResponse> => {
    try {
      const response = await api.delete(`/personel/${userId}/with-user`);
      return {
        success: true,
        status: response.status,
        message: "Pengguna berhasil dinonaktifkan",
        data: response.data,
      };
    } catch (error) {
      const axiosError = error as AxiosError<ErrorResponse>;
      return {
        success: false,
        status: axiosError.response?.status || 500,
        message: formatError(axiosError),
      };
    }
  },

  updatePersonel: async (
    id: string,
    data: UpdatePersonelRequest,
  ): Promise<ActionResponse> => {
    try {
      const response = await api.put(`/personel/${id}`, data);
      return {
        success: true,
        status: response.status,
        message: "Personel berhasil diperbarui",
        data: response.data, // personel terbaru: { id, pangkat, jabatan, full_name, ... }
      };
    } catch (error) {
      const axiosError = error as AxiosError<ErrorResponse>;
      return {
        success: false,
        status: axiosError.response?.status || 500,
        message: formatError(axiosError),
      };
    }
  },

  // Ubah password -> /auth/password/change (nonce ditambahkan otomatis oleh interceptor)
  changePassword: async (
    data: ChangePasswordRequest,
  ): Promise<ActionResponse> => {
    try {
      const response = await api.post("/auth/password/change", data);
      return {
        success: true,
        status: response.status,
        message: "Password berhasil diubah",
        data: response.data,
      };
    } catch (error) {
      const axiosError = error as AxiosError<ErrorResponse>;
      return {
        success: false,
        status: axiosError.response?.status || 500,
        message: formatError(axiosError),
      };
    }
  },

  // ✅ Upload foto → response adalah JSON, bukan blob
  updatePersonelPhoto: async (id: string, file: File): Promise<string> => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await api.put(`/personel/${id}/photo`, formData);

      // Response: { photo_url: "/api/v1/personel/.../photo", content_type, size }
      return response.data.photo_url as string;
    } catch (error) {
      const axiosError = error as AxiosError<ErrorResponse>;
      throw new Error(formatError(axiosError));
    }
  },

  // ✅ Get foto → fetch sebagai blob, tapi force type image/jpeg
  getPersonelPhoto: async (id: string): Promise<string | null> => {
    try {
      const response = await api.get(`/personel/${id}/photo`, {
        responseType: "blob",
      });
      // Override content-type jika perlu (server sering return octet-stream)
      const blob = new Blob([response.data], { type: "image/jpeg" });
      return URL.createObjectURL(blob);
    } catch {
      return null;
    }
  },
};